import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop reordering primitives built on @dnd-kit.
 *
 * Usage A — table layout (4 table pages: Categories, Products, Blogs, Careers):
 *   <SortableProvider items setItems reorderEndpoint>
 *     <table>
 *       <thead>...</thead>
 *       <tbody>
 *         <SortableItems items={items} strategy="vertical">
 *           {items.map(it => <SortableItem key id as="tr">{(handle)=> ...}</SortableItem>)}
 *         </SortableItems>
 *       </tbody>
 *     </table>
 *   </SortableProvider>
 *
 * Usage B — grid layout (Clients, Sliders):
 *   <SortableList items setItems reorderEndpoint layout="grid">
 *     {items.map(it => <SortableItem key id as="div">...</SortableItem>)}
 *   </SortableList>
 *
 * Why two patterns: dnd-kit's <DndContext> renders hidden announcer <div>s for
 * a11y. Placing those inside <tbody> is invalid HTML, so for tables we hoist
 * the DndContext above the <table> with <SortableProvider>.
 */

export function SortableProvider({ items, setItems, reorderEndpoint, children }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newList = arrayMove(items, oldIndex, newIndex);
    setItems(newList);
    try {
      await api.put(reorderEndpoint, { ids: newList.map((i) => i.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error(formatApiError(e));
      setItems(items);
    }
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      {children}
    </DndContext>
  );
}

export function SortableItems({ items, strategy = "vertical", children }) {
  const s = strategy === "grid" ? rectSortingStrategy : verticalListSortingStrategy;
  return (
    <SortableContext items={items.map((i) => i.id)} strategy={s}>
      {children}
    </SortableContext>
  );
}

/** Convenience combo for grid/div layouts — wraps DndContext + SortableContext. */
export function SortableList({ items, setItems, reorderEndpoint, layout = "list", children }) {
  return (
    <SortableProvider items={items} setItems={setItems} reorderEndpoint={reorderEndpoint}>
      <SortableItems items={items} strategy={layout === "grid" ? "grid" : "vertical"}>
        {children}
      </SortableItems>
    </SortableProvider>
  );
}

export function SortableItem({ id, as = "div", className = "", children, "data-testid": testId }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#F7F8F6" : undefined,
  };
  const handleProps = { ...attributes, ...listeners };
  const Component = as;
  return (
    <Component ref={setNodeRef} style={style} className={className} data-testid={testId}>
      {typeof children === "function" ? children(handleProps) : children}
    </Component>
  );
}

export function DragHandle({ handleProps, className = "" }) {
  return (
    <button
      type="button"
      {...handleProps}
      className={`cursor-grab active:cursor-grabbing text-[#4B5563] hover:text-[#C2410C] touch-none ${className}`}
      title="Drag to reorder"
      data-testid="drag-handle"
      onClick={(e) => e.preventDefault()}
    >
      <DotsSixVertical size={18} weight="bold" />
    </button>
  );
}
